import { Box, Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { PersonNameLink } from "src/components/Navigation/PersonNameLink";
import { Glossary } from "src/components/Text/Glossary";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { formatBirthDate } from "src/services/format";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";

type Organization = { path: string };

interface DuplicateStudentCardProps {
  applicant: ApplicantDuplicate;
  organization: RD.RemoteData<unknown, Organization>;
  // Select this existing student in place instead of navigating away. Its id is
  // used to link the guardians being added.
  onUse: (applicant: ApplicantDuplicate) => void;
}

export function DuplicateStudentCard({
  applicant,
  organization,
  onUse,
}: DuplicateStudentCardProps) {
  const fullName = [applicant.first_name, applicant.last_name]
    .filter(Boolean)
    .join(" ");
  const born = formatBirthDate(applicant.birth_date);

  return (
    <Box
      border="1px solid"
      borderColor="orange.200"
      borderLeft="4px solid"
      borderLeftColor="orange.400"
      borderRadius="md"
      padding={4}
      backgroundColor="white"
    >
      <Flex
        alignSelf="flex-start"
        display="inline-flex"
        align="flex-start"
        gap={2}
        maxW="100%"
        backgroundColor="orange.100"
        color="orange.800"
        borderRadius="md"
        paddingX={3}
        paddingY={1}
        marginBottom={3}
      >
        <Box
          width={2}
          height={2}
          mt={1.5}
          flexShrink={0}
          borderRadius="full"
          backgroundColor="orange.500"
        />
        <Text fontSize="sm" fontWeight="600" minW={0}>
          Strong match: name + date of birth
        </Text>
      </Flex>

      <Text fontSize="lg" fontWeight="700">
        {fullName}
      </Text>

      <Text fontSize="sm" color="gray.700">
        {born && <>Born {born}</>}
        {applicant.guardians.length > 0 && (
          <>
            {born ? " · " : ""}
            Child of{" "}
            {applicant.guardians.map((guardian, index) => (
              <React.Fragment key={guardian.id}>
                {index > 0 && ", "}
                <PersonNameLink
                  to={Url.OrgAdmin.Parents.edit(organization, guardian.id)}
                >
                  {[guardian.first_name, guardian.last_name]
                    .filter(Boolean)
                    .join(" ")}
                </PersonNameLink>
              </React.Fragment>
            ))}
          </>
        )}
      </Text>

      <Text fontSize="sm" color="gray.700">
        ID: {applicant.id}
      </Text>
      {applicant.reference_id && (
        <Text fontSize="sm" color="gray.700">
          Ref ID {applicant.reference_id}
        </Text>
      )}

      <Button
        type="button"
        size="sm"
        marginTop={4}
        onClick={() => onUse(applicant)}
      >
        <Glossary>Use this student</Glossary>
      </Button>
    </Box>
  );
}
